import type { History } from "history";
import { type ReactNode, useMemo } from "react";
import { useHistory, useLocation as useV5Location } from "react-router-dom";
import {
	type Location,
	type NavigationType,
	type Navigator,
	Route,
	Router,
	Routes,
	type To,
	parsePath,
} from "react-router-dom-v5-compat";

// Drop-in for the compat package's CompatRouter (WBC3-2225): v6 hooks on top of the mount's
// react-router v5 router. The package keeps its own history.listen copy of the location, which
// goes stale whenever a v5 <Redirect> navigates while it isn't listening yet or mid-notification,
// and routes mounted by a navigation outside a React batch see the previous location. Reading
// the v5 router's location re-renders this in the same pass as the routes below it.
export function CompatRouter({ children }: { children: ReactNode }) {
	const history = useHistory();
	const location = useV5Location();
	const navigator = useMemo(() => stateKeepingNavigator(history), [history]);

	return (
		<Router
			navigationType={history.action as NavigationType}
			location={location as Location}
			navigator={navigator}
		>
			<Routes>
				<Route path="*" element={children} />
			</Routes>
		</Router>
	);
}

// v6 navigate passes state as a second argument, which hash history 4 drops; inside the
// location object it is kept, as v5 callers did with push({ pathname, state }).
function stateKeepingNavigator(history: History): Navigator {
	const toLocation = (target: To) => (typeof target === "string" ? parsePath(target) : target);

	return {
		createHref: target => history.createHref(toLocation(target)),
		// eslint-disable-next-line id-length -- name fixed by v6's Navigator interface
		go: delta => history.go(delta),
		push: (target, state) => history.push({ ...toLocation(target), state }),
		replace: (target, state) => history.replace({ ...toLocation(target), state }),
	};
}
